import * as Api from '@tableau/api-internal-contract-js';
import * as PresLayerEnums from '../../gen-src/preslayer/Enums';
import * as PresLayer from '../../gen-src/preslayer/PresModels';
import { PresLayerToApiEnumMappings as EnumMappings, ShouldThrow } from './EnumMappings';
import { PresLayerOptionalCompatibility } from './PresLayerOptionalCompatibility';

/**
 * Class containing helper methods for converting from PresLayerPresModels to their ExtensionsApi or EmbeddingApi PresModels equivalents
 *
 * @export
 * @class PresLayerToSharedApiConverter
 */
export class PresLayerToSharedApiConverter {
  public static convertFieldCaptionPair(plFieldCaptionPair: PresLayer.FieldCaptionPairPresModel): string {
    return plFieldCaptionPair.fn;
  }

  public static convertVisualId(plVisualId: PresLayer.VisualIDPresModel): Api.VisualId {
    const result = {
      worksheet: plVisualId.worksheet,
      ...(plVisualId.dashboard !== undefined && { dashboard: plVisualId.dashboard }),
      ...(plVisualId.storyboard !== undefined && { storyboard: plVisualId.storyboard }),
      ...(plVisualId.flipboardZoneId !== undefined && { flipboardZoneID: plVisualId.flipboardZoneId }),
      ...(plVisualId.storyPointId !== undefined && { storyPointID: plVisualId.storyPointId }),
    };

    return result;
  }

  public static convertFilterEvent(plFilterEvent: PresLayer.FilterChangeEventPresModel): Api.FilterEvent {
    const result: Api.FilterEvent = {
      visualId: PresLayerToSharedApiConverter.convertVisualId(plFilterEvent.visualIdPresModel),
      fieldName: plFilterEvent.fieldCaption,
      fieldId: plFilterEvent.globalFieldName,
    };

    return result;
  }

  public static convertSummaryDataEvent(plSummaryDataEvent: PresLayer.SummaryDataChangeEventPresModel): Api.SummaryDataEvent {
    const result: Api.SummaryDataEvent = {
      visualId: PresLayerToSharedApiConverter.convertVisualId(plSummaryDataEvent.visualIdPresModel),
    };

    return result;
  }

  public static convertDashboardPresModelToZones(
    plDashboard: PresLayer.DashboardPresModel | null,
    worksheetUrls?: Map<string, string>,
  ): Array<Api.DashboardZone> {
    if (!plDashboard) {
      // plDashboard is null for a blank Story.
      // plDashboard.zones is undefined for a blank Dashboard.
      return new Array<Api.DashboardZone>();
    }

    return PresLayerToSharedApiConverter.convertZoneMapToZones(plDashboard.zones, worksheetUrls);
  }

  public static convertZoneMapToZones(
    zones: { [key: string]: PresLayer.ZonePresModel } | undefined,
    worksheetUrls?: Map<string, string>,
  ): Array<Api.DashboardZone> {
    const result = new Array<Api.DashboardZone>();
    if (!zones) {
      return result;
    }

    for (const zoneKey of Object.keys(zones)) {
      const zone = zones[zoneKey];

      const zoneCommon = PresLayerOptionalCompatibility.optionalToRequiredUnsafeCast(zone.zoneCommon);

      const apiZone: Api.DashboardZone = {
        name: PresLayerOptionalCompatibility.optionalToRequiredUnsafeCast(zoneCommon.name),
        zoneId: zone.zoneId,
        zoneType: EnumMappings.zoneType.convert(zoneCommon.zoneType, ShouldThrow.No),
        height: zoneCommon.h,
        width: zoneCommon.w,
        x: zoneCommon.x,
        y: zoneCommon.y,
        isFloating: zoneCommon.isFloating,
        isVisible: zone.isVisible,
      };

      // Fix up Viz & Extension zones
      if (zoneCommon.zoneType === PresLayerEnums.ZoneType.Viz) {
        const worksheet = PresLayerOptionalCompatibility.optionalToRequiredUnsafeCast(zone.worksheet);
        const sheetInfo: Api.SheetInfo = {
          name: worksheet,
          sheetType: Api.SheetType.Worksheet,
        };
        // worksheetUrls is only used by Embedding
        if (worksheetUrls?.has(worksheet)) {
          sheetInfo.url = worksheetUrls.get(worksheet);
        }
        apiZone.sheetInfo = sheetInfo;
      } else if (
        zoneCommon.zoneType === PresLayerEnums.ZoneType.DashboardObject &&
        zoneCommon.dashboardObjectType === PresLayerEnums.DocDashboardObjectTypes.DBO_Extension
      ) {
        apiZone.zoneType = Api.DashboardObjectType.Extension;
      }

      result.push(apiZone);
    }

    return result;
  }

  public static convertDashboardToZones(
    plDashboardZoneInfos: PresLayer.DashboardZoneInfoPresModel[],
    worksheetUrls?: Map<string, string>,
  ): Array<Api.DashboardZone> {
    const result = new Array<Api.DashboardZone>();

    for (const zone of plDashboardZoneInfos) {
      const apiZone: Api.DashboardZone = {
        zoneId: zone.zoneId,
        name: PresLayerOptionalCompatibility.optionalToRequiredUnsafeCast(zone.name),
        zoneType: EnumMappings.zoneType.convert(zone.zoneType, ShouldThrow.No),
        height: zone.h,
        width: zone.w,
        x: zone.x,
        y: zone.y,
        isFloating: zone.isFloating,
        isVisible: zone.isVisible,
      };

      // Fix up Viz & Extension zones
      const worksheet = PresLayerOptionalCompatibility.optionalToRequiredUnsafeCast(zone.worksheet);
      if (zone.zoneType === PresLayerEnums.ZoneType.Viz || zone.zoneType === PresLayerEnums.ZoneType.Filter) {
        const sheetInfo: Api.SheetInfo = {
          name: worksheet,
          sheetType: Api.SheetType.Worksheet,
        };
        // worksheetUrls is only used by Embedding
        if (worksheetUrls && worksheetUrls[worksheet]) {
          sheetInfo.url = worksheetUrls[worksheet];
        }
        if (zone.zoneType === PresLayerEnums.ZoneType.Filter) {
          apiZone.fieldId = zone.fieldId;
        }
        apiZone.sheetInfo = sheetInfo;
      } else if (
        zone.zoneType === PresLayerEnums.ZoneType.DashboardObject &&
        zone.dashboardObjectType === PresLayerEnums.DocDashboardObjectTypes.DBO_Extension
      ) {
        apiZone.zoneType = Api.DashboardObjectType.Extension;
      }

      result.push(apiZone);
    }

    return result;
  }

  private static convertFieldBase(plField: PresLayer.FieldPresModel): Api.FieldBase {
    const fieldColumn = plField as PresLayer.FieldColumnPresModel;
    const groupColumn = plField as PresLayer.GroupPresModel;

    let aggregation = Api.FieldAggregationType.None;
    let role = Api.FieldRoleType.Unknown;
    const isCalculated = fieldColumn.isCalculated || false;

    // Column Type is already a required field for the customer layer, but not for the pres layer
    fieldColumn.fieldType = fieldColumn.fieldType || PresLayerEnums.FieldType.Unknown;
    fieldColumn.defaultAggregation = fieldColumn.defaultAggregation || PresLayerEnums.AggType.None;
    fieldColumn.defaultFieldRole = fieldColumn.defaultFieldRole || PresLayerEnums.FieldRole.Unknown;

    if (fieldColumn.type === PresLayerEnums.FieldRole.Dimension) {
      aggregation = Api.FieldAggregationType.None;
      role = Api.FieldRoleType.Dimension;
    } else {
      aggregation = EnumMappings.fieldAggregationType.convert(fieldColumn.defaultAggregation);
      role = EnumMappings.fieldRoleType.convert(fieldColumn.defaultFieldRole);
    }

    let result: Api.FieldBase = {
      name: PresLayerOptionalCompatibility.optionalToRequiredUnsafeCast(plField.description),
      description: plField.userCaption,
      dataType: fieldColumn.dataType === undefined ? Api.DataType.Unknown : EnumMappings.dataType.convert(fieldColumn.dataType),
      aggregation: aggregation,
      role: role,
      columnType: EnumMappings.columnType.convert(fieldColumn.fieldType),
      isCalculatedField: isCalculated,
      isCombinedField: !!groupColumn.isCombinedField,
      isGenerated: !!plField.isGenerated,
      isGeospatial: !!fieldColumn.isGeospatial,
      isHidden: !!plField.isHidden,
      // Renaming the field to make it more intuitive to clients.
      // Internally, "layered" refers to the fact that additional data is layered over the fields of the published datasource.
      isPresentOnPublishedDatasource: !!groupColumn.isLayered,
    };

    return result;
  }

  public static convertField(plField: PresLayer.FieldPresModel): Api.Field {
    let result: Partial<Api.Field> = PresLayerToSharedApiConverter.convertFieldBase(plField);

    result.id = plField.name;

    return result as Api.Field;
  }

  public static convertFieldInstance(plField: PresLayer.FieldPresModel): Api.FieldInstance {
    let result: Partial<Api.FieldInstance> = PresLayerToSharedApiConverter.convertFieldBase(plField);
    result.fieldId = plField.fn;
    return result as Api.FieldInstance;
  }

  public static convertVisualSpecification(vsPresModel: PresLayer.VisualSpecificationPresModel): Api.VisualSpecification {
    return {
      rowFields: vsPresModel.rowFields.map((f) => PresLayerToSharedApiConverter.convertFieldInstance(f)),
      columnFields: vsPresModel.columnFields.map((f) => PresLayerToSharedApiConverter.convertFieldInstance(f)),
      activeMarksSpecificationIndex: vsPresModel.activeMarksSpecIdx,
      marksSpecifications: vsPresModel.marksSpecifications.map((x) => {
        return {
          primitiveType: EnumMappings.primitiveType.convert(x.primitiveType),
          encodings: x.fieldEncodingPairs.map((e) => {
            const et = EnumMappings.encodingType.convert(e.encodingTypePresModel.encodingType);
            return {
              field: PresLayerToSharedApiConverter.convertFieldInstance(e.field),
              id: e.encodingTypePresModel.customEncodingTypeId?.length > 0 ? e.encodingTypePresModel.customEncodingTypeId : et,
              type: et,
            };
          }),
        };
      }),
    };
  }
}
